import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASEURL, ITEMS } from "./../api/endpoints.js";
import convertToFormData from "./../utils/convertToFormData.js";
import getCookie from "./../utils/getCookie.js";
import { longCacheTime, shortCacheTime } from "./../config/constants";

const transformProductData = (data) => {
  return {
    Id: data.ProID,
    NameAr: data.Pro_AR_Name,
    NameEn: data.Pro_EN_Name,
    Father: data.CategoryAr,
    CatID: data.CatID,
    Tag: data.Tag,
    Question1: data.Question1,
    Question2: data.Question2,
    Question3: data.Question3,
    Question4: data.Question4,
    Question5: data.Question5,
    Compo: data.Compo,
    ForeColor: data.ForeColor,
    BackColor: data.BackColor,
    PreparationTime: data.PreparationTime,
    ImgPath: data.ImgPath,
    Printer: data.Printer,
    Printer2: data.Printer2,
    Barcode: data.Barcode,
    Price: data.Price,
    Price2: data.Price2,
    Price3: data.Price3,
    Price4: data.Price4,
    Warehouse: data.TagDesc,
    UnitID: data.UnitID,
    TaxPercentage: data.TaxPercentage,
    Discountable: data.Discountable,
    RawMaterial: data.RawMaterial, // أضف هذا السطر
    StandardItem: data.StandardItem, // أضف هذا السطر
    CompositeMaterial: data.CompositeMaterial,
    IsService: data.IsService,
    IsActive: data.IsActive,
    Saleable: data.Saleable,
    Taxable: data.Taxable,
    Icon: data.Icon,
  };
};

export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASEURL + ITEMS,
    prepareHeaders: (headers) => {
      headers.set("Authorization", `Bearer ${getCookie("accessToken")}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getCurrentProductkey: builder.query({
      query: (categoryId) => ({
        url: `/GetCurrentKey?fatherId=${categoryId}`,
      }),
      transformResponse: (response) => response.Response,
      providesTags: ["Product_id"],
    }),
    getAllProducts: builder.query({
      query: ({ pageNumber, pageSize }) => ({
        url: `/GetAll?paging.PageNumber=${pageNumber}&paging.PageSize=${pageSize}`,
      }),
      keepUnusedDataFor: longCacheTime,
      providesTags: ['products'],
      transformResponse: (response) => {
        const data = response.Response || response;
        if (Array.isArray(data)) {
          return data.map((item) => transformProductData(item));
        } else {
          return [];
        }
      },
    }),
    getProductByType: builder.query({
      query: ({ pageNumber, pageSize, branch }) => ({
        url: `/GetAll?paging.PageNumber=${pageNumber}&paging.PageSize=${pageSize}&Warehouse=${branch}`,
      }),
      keepUnusedDataFor: longCacheTime,
      providesTags: ['products'],
      transformResponse: (response) => {
        const rawData = response.Response || response;

        if (Array.isArray(rawData)) {
          return rawData.map((item) => transformProductData(item));
        }
        return [];
      },
    }),
    // داخل الـ endpoints في productsApi.js

    getProductUnitsById: builder.query({
      query: (id) => ({
        url: `/GetItemUnitsById?Id=${id}`,
      }),
      transformResponse: (response) => {
        return response.Response || [];
      },
    }),

    getAllItemUnits: builder.query({
      query: () => ({
        url: `/ApiGetAllUnits`,
      }),
      transformResponse: (response) => {
        return response.Response || [];
      },
    }),
    getCompositeComponentsById: builder.query({
      query: (id) => ({
        url: `/AppGetItemRecipeById?Id=${id}`,
      }),
      keepUnusedDataFor: longCacheTime,
      transformResponse: (response) => response.Response,
      providesTags: ["Components"],
    }),
    getProductsByCategory: builder.query({
      query: (id) => ({
        url: `/ApiGetByCategory?FatherID=${id}`,
      }),
      keepUnusedDataFor: shortCacheTime,
      transformResponse: (response) => response.Response,
    }),
    getProductById: builder.query({
      query: (id) => ({
        url: `/GetById?ProductId=${id}`,
      }),
    }),
    addProduct: builder.mutation({
      query: ({ type, product }) => {
        // داخل addProduct Mutation
        const formattedItemsUnit = (product.ItemsUnit || []).map(
          (unit, index) => ({
            ItemID: String(product.Id),
            UnitID: Number(unit.UnitID),
            Factor: Number(unit.Factor) || (index === 0 ? 1 : 10), // افتراضياً 1 لأول وحدة
            IsSmall: index === 0 ? true : false,
            Barcode: String(unit.Barcode || product.Barcode || ""),
          }),
        );

        // ===============================
        // StandardItem & RawMaterial (JSON)
        // ===============================
        if (type === "StandardItem" || type === "RawMaterial") {
          return {
            url: `/Insert${type}`,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: {
              ...product,
              Id: String(product.Id),
              Father: String(product.Father),
              Barcode: String(product.Barcode),
              Warehouse: String(product.Warehouse),
              Price: Number(product.Price) || 0,
              Price2: Number(product.Price2) || 0,
              Price3: Number(product.Price3) || 0,
              Price4: Number(product.Price4) || 0,
              TaxPercentage: Number(product.TaxPercentage) || 0,
              UnitID: Number(product.UnitID || 1),
              ItemsUnit: formattedItemsUnit,
            },
          };
        }

        // ===============================
        // Composite (FormData)
        // ===============================
        const formData = new FormData();

        Object.keys(product).forEach((key) => {
          if (key !== "ItemsUnit") {
            formData.append(key, product[key]);
          }
        });

        formattedItemsUnit.forEach((unit, index) => {
          Object.keys(unit).forEach((key) => {
            formData.append(`ItemsUnit[${index}].${key}`, unit[key]);
          });
        });

        return {
          url: `/Insert${type}`,
          method: "POST",
          body: formData,
        };
      },

      invalidatesTags: ["products", "Product_id"],
    }),

    getStandardAndRawMaterials: builder.query({
      query: ({ Warehouse, pageNumber, pageSize }) => ({
        url: `/StandardAndRawMaterial?Warehouse=${Warehouse}&paging.PageNumber=${pageNumber}&paging.PageSize=${pageSize}`,
      }),
      keepUnusedDataFor: longCacheTime,
      transformResponse: (response) => {
        const data = response.Response || response;
        if (Array.isArray(data)) {
          return data.map((item) => transformProductData(item));
        } else {
          return [];
        }
      },
    }),
    updateProduct: builder.mutation({
      query: ({ product }) => ({
        url: `/Update`,
        method: "POST",
        body: product,
      }),
      invalidatesTags: ["products"],
    }),
    updateProductHeader: builder.mutation({
      query: ({ product }) => ({
        url: `/Update`,
        method: "POST",
        body: convertToFormData(product),
      }),
      invalidatesTags: ["products"],
    }),
    deleteProduct: builder.mutation({
      query: (id) => ({
        url: `/Delete`,
        method: "POST",
        body: convertToFormData(id),
      }),
      invalidatesTags: ['products'],
    }),
    deleteProductUnit: builder.mutation({
      query: (component) => ({
        url: `/DeleteItemUnit`,
        method: "POST",
        body: convertToFormData(component),
      }),
      invalidatesTags: ["products"],
    }),
    addComponent: builder.mutation({
      query: (component) => ({
        url: `/InsertRecipe`,
        method: "POST",
        body: convertToFormData(component),
      }),
      invalidatesTags: ["Components"],
    }),
    updateComponent: builder.mutation({
      query: (component) => ({
        url: `/UpdateItemRecipe`,
        method: "POST",
        body: convertToFormData(component),
      }),
      invalidatesTags: ["Components"],
    }),
    deleteComponent: builder.mutation({
      query: ({ ItemID, SubItem }) => ({
        url: `/DeleteItemRecipe`,
        method: "POST",
        body: convertToFormData({ ItemID, SubItem }),
      }),
      invalidatesTags: ["Components"],
    }),
    getProductsCost: builder.query({
      query: ({ CatID, Warehouse }) => ({
        url: `/GetItemsCost?CateID=${CatID}&Warehouse=${Warehouse}`,
      }),
      transformResponse: (response) => response.Response,
    }),
    getProductsCosts: builder.query({
      query: ({ CateID, Warehouse }) => ({
        url: `/GetItemsCost?CateID=${CateID}&Warehouse=${Warehouse}`,
      }),
      transformResponse: (response) => response.Response,
    }),
  }),
});

export const {
  useLazyGetCurrentProductkeyQuery,
  useGetProductByIdQuery,
  useGetProductByTypeQuery,
  useAddProductMutation,
  useGetCurrentProductkeyQuery,
  useDeleteProductMutation,
  useAddComponentMutation,
  useGetProductsByCategoryQuery,
  useGetCompositeComponentsByIdQuery,
  useLazyGetProductsCostsQuery,
  useUpdateProductMutation,
  useGetProductUnitsByIdQuery,
  useGetAllItemUnitsQuery,
  useLazyGetProductsByCategoryQuery,
  useUpdateComponentMutation,
  useDeleteComponentMutation,
  useGetAllProductsQuery,
  useGetStandardAndRawMaterialsQuery,
  useLazyGetStandardAndRawMaterialsQuery,
  useLazyGetProductUnitsByIdQuery,
  useGetProductsCostsQuery,
  useDeleteProductUnitMutation,
  // useGetProductUnitsQuery,
} = productsApi;
